import React, {
    FC,
    memo,
    ReactElement,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {createPortal} from 'react-dom';
import styled from 'styled-components';
import root from 'window-or-global';

import StatusSnackbar, {
    Statuses
} from '../../../../../pages/fortnite-games/components/StatusSnackbar';
import {formatFileName} from '../../../../../utils/Utils';
import useTrackExportErrors from './useTrackExportErrors';
import {PDF_PAGE_OPTIONS} from '../constants';

export const PDF_CONTAINER_WIDTH = 1123;
const PDF_CONTAINER_HEIGHT = 794;

const RenderContainer = styled.figure<{height: number}>`
    width: ${PDF_CONTAINER_WIDTH}px;
    height: ${p => p.height}px;

    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;

    margin: 0;
    padding: ${p => p.theme.muiTheme.spacing(2)}px;

    display: flex;
    align-items: center;
    justify-content: center;

    & > * {
        width: 100%;
    }

    // Fix broken paddings for chart in result picture
    @media (max-width: 768px) {
        .recharts-responsive-container {
            width: calc(100% - ${p => p.theme.muiTheme.spacing(2)}px) !important;
        }
    }
`;

export interface PageOptionsProps {
    orientation: string;
    pageFormat: number[] | string;
}

export interface DownloadProps {
    name: string;
    options?: PageOptionsProps;
    id?: string[];
}

export interface PDFExportContextValue {
    status: Statuses;
    resetError: () => void;
    registerArea: (id: string, reactElement: ReactElement, index?: number) => void;
    unregisterArea: (id: string) => void;
    onDownload: ({name, options, id}: DownloadProps) => void;
}

export const PDFExportContext = React.createContext<PDFExportContextValue>({
    status: Statuses.idle,
    resetError: () => {},
    registerArea: () => {},
    unregisterArea: () => {},
    onDownload: () => {}
});

export interface PDFPictureTrapProps {
    children: ReactElement;
    id: string;
    index?: number;
}

/* PDFPictureTrap:
 * registers it's children nodes in PDFExportProvider registry,
 * it let us ability to render this nodes again in prepared container
 * */
export const PDFPictureTrap: FC<PDFPictureTrapProps> = ({
    children,
    id,
    index
}: PDFPictureTrapProps) => {
    const {registerArea, unregisterArea} = useContext(PDFExportContext);

    useEffect(() => {
        registerArea(id, children, index);

        return () => unregisterArea(id);
    }, [children, id]);

    return <>{children}</>;
};

export interface PortalContainerProps {
    children: ReactNode;
    isRender: boolean;
    height: number;

    onReady: (element: HTMLElement) => void;
}

/* PortalContainer:
 * the hidden container with specific size and styles where
 * we can render selected nodes, to make it possible to
 * render desktop chart view even in mobile screen sizes
 * */
const PortalContainer = memo<PortalContainerProps>(
    ({children, isRender, height, onReady}: PortalContainerProps) => {
        const containerRef = useRef();

        useEffect(() => {
            if (isRender && children) {
                // Call onReady asynchronously to all canvases were built
                setTimeout(() => onReady(containerRef.current), 0);
            }
        }, [isRender, children]);

        if (!root.document || !isRender) {
            return null;
        }

        return createPortal(
            <RenderContainer ref={containerRef} height={height}>
                {children}
            </RenderContainer>,
            root.document.body
        );
    }
);

class AreaRegistry {
    areas: Map<string, [ReactElement, number | undefined]>;

    constructor() {
        this.areas = new Map();
    }

    register = (id: string, reactElement: ReactElement, index?: number) => {
        this.areas.set(id, [reactElement, index]);
    };

    unregister = (id: string) => {
        this.areas.delete(id);
    };

    getList = () => Array.from(this.areas.values());

    getListById = (id: string[]) => id.map(key => this.areas.get(key)).map(([element]) => element);
}

export interface PDFExportProviderProps {
    children: ReactNode;
}

export const PDFExportProvider: FC<PDFExportProviderProps> = ({
    children
}: PDFExportProviderProps) => {
    const renderedCanvases = useRef<HTMLCanvasElement[]>([]);
    const {current: areasRegistry} = useRef<AreaRegistry>(new AreaRegistry());
    const fileName = useRef<string>('untitled');
    const inProgress = useRef<boolean>(false);
    const pageOptions = useRef<PageOptionsProps>(PDF_PAGE_OPTIONS);

    const [status, setStatus] = useState<Statuses>(Statuses.idle);
    const [areasToRender, setAreasToRender] = useState<ReactElement[]>([]);
    const {trackPDFError} = useTrackExportErrors();

    const resetError = useCallback(() => {
        if (!inProgress.current) {
            setStatus(Statuses.idle);
        }
    }, [setStatus]);

    const onDownload = useCallback(
        ({name, options, id}: DownloadProps) => {
            if (inProgress.current) {
                return;
            }

            fileName.current = name;
            inProgress.current = true;

            if (options) {
                pageOptions.current = options;
            }

            setStatus(Statuses.loading);

            if (id) {
                setAreasToRender(() => areasRegistry.getListById(id));
            } else {
                setAreasToRender(
                    areasRegistry
                        .getList()
                        .filter(([element, index = 0]) => index >= 0 && element)
                        .sort(([, indexA], [, indexB]) => indexA - indexB)
                        .map(([element]) => element)
                );
            }
        },
        [setStatus, setAreasToRender]
    );

    const cleanup = () => {
        renderedCanvases.current = [];
        setAreasToRender([]);
        inProgress.current = false;
    };

    const handleAreaRendered = async (element: HTMLElement) => {
        try {
            // @ts-ignore
            const module = await import(
                /* webpackChunkName: "PDFUtils" */ '../../../../../utils/PDFUtils'
            );
            const canvas = await module.createCanvasFromElement({
                element,
                backgroundColor: '#000000'
            });
            renderedCanvases.current.push(canvas);
            const [, ...restAreas] = areasToRender;

            setAreasToRender(restAreas);
        } catch (error) {
            trackPDFError(error);
            setStatus(Statuses.error);
            cleanup();
            console.error(error);
        }
    };

    const buildPDF = async () => {
        try {
            // @ts-ignore
            const module = await import(
                /* webpackChunkName: "PDFUtils" */ '../../../../../utils/PDFUtils'
            );
            module
                .generatePDFDocument(renderedCanvases.current, pageOptions.current)
                .save(formatFileName(fileName.current));

            setStatus(Statuses.idle);
        } catch (err) {
            setStatus(Statuses.error);
            console.error(err);
        } finally {
            cleanup();
        }
    };

    const handleRetry = () => {
        if (!inProgress.current) {
            resetError();

            // Without timeout it seems like nothing happens
            setTimeout(() => onDownload(fileName.current, pageOptions.current), 500);
        }
    };

    useLayoutEffect(() => {
        if (!areasToRender.length && status === Statuses.loading) {
            buildPDF();
        }
    }, [areasToRender]);

    const value = useMemo(
        () => ({
            status,
            registerArea: areasRegistry.register,
            unregisterArea: areasRegistry.unregister,
            resetError,
            onDownload
        }),
        [status]
    );

    const {
        pageFormat: [_width, height]
    } = pageOptions.current;
    const containerHeight = typeof height === 'number' ? height : PDF_CONTAINER_HEIGHT;

    return (
        <>
            <PortalContainer
                key="portal-container"
                isRender={Boolean(areasToRender.length)}
                height={containerHeight}
                onReady={handleAreaRendered}>
                {areasToRender[0]}
            </PortalContainer>
            <StatusSnackbar status={status} onRetry={handleRetry} onClose={resetError} />
            <PDFExportContext.Provider value={value}>{children}</PDFExportContext.Provider>
        </>
    );
};
